import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { vendorDashboardApi, VendorDashboardStats, VendorDashboardTimeRange } from "./dashboard.api";

interface VendorDashboardState {
  stats: VendorDashboardStats | null;
  dashboardTimeRange: VendorDashboardTimeRange;
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
}

const initialState: VendorDashboardState = {
  stats: null,
  dashboardTimeRange: "30d",
  loading: false,
  error: null,
  lastUpdated: null,
};

export const fetchVendorDashboardStats = createAsyncThunk(
  "vendorDashboard/fetchStats",
  async (timeRange: VendorDashboardTimeRange | undefined, { getState, rejectWithValue }) => {
    try {
      const range = timeRange ?? (getState() as { vendorDashboard: VendorDashboardState }).vendorDashboard.dashboardTimeRange;
      return await vendorDashboardApi.getDashboardStats(range);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : "Failed to fetch dashboard stats");
    }
  }
);

const vendorDashboardSlice = createSlice({
  name: "vendorDashboard",
  initialState,
  reducers: {
    setDashboardTimeRange: (state, action: PayloadAction<VendorDashboardTimeRange>) => {
      state.dashboardTimeRange = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearStats: (state) => {
      state.stats = null;
      state.lastUpdated = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch dashboard stats
      .addCase(fetchVendorDashboardStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVendorDashboardStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
        state.lastUpdated = new Date().toISOString();
        state.error = null;
      })
      .addCase(fetchVendorDashboardStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setDashboardTimeRange, clearError, clearStats } = vendorDashboardSlice.actions;
export default vendorDashboardSlice.reducer;
